import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    FaCheckCircle, FaTimesCircle, FaExclamationTriangle,
    FaLock, FaLockOpen, FaSync, FaInfoCircle,
} from 'react-icons/fa';
import MainScreenHeader from '../../../common/Header/MainScreenHeader';
import Toolbar, { ToolbarButtonConfig, ToolbarFilterConfig } from '../../../common/Toolbar/Toolbar';
import ErrorMessage from '../../../common/ErrorMessage/ErrorMessage';
import StatsBar from '../../../common/StatsBar/StatsBar';
import { FiscalYearRestDataProvider, FiscalYearClosingStatus } from '../../../../data/FiscalYearRestDataProvider';
import { FiscalYear, FiscalYearState } from '../../../../models/FiscalYear';
import '../../../../styles/GenericSiteStyles.scss';
import './YearEndClosing.scss';

// ── Types ─────────────────────────────────────────────────────────────────────

type CheckResult = 'pass' | 'fail' | 'warn' | 'info';

interface CheckItem {
    key: string;
    title: string;
    description: string;
    result: CheckResult;
    badge?: string;
    details?: string[];
}

// ── Helper ────────────────────────────────────────────────────────────────────

const fmt = (v: number) =>
    v.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtEuro = (v: number) => `€\u00a0${fmt(v)}`;

const resultIcon = (r: CheckResult) => {
    switch (r) {
        case 'pass': return <FaCheckCircle style={{ color: '#28a745' }} />;
        case 'fail': return <FaTimesCircle style={{ color: '#dc3545' }} />;
        case 'warn': return <FaExclamationTriangle style={{ color: '#ffc107' }} />;
        case 'info': return <FaInfoCircle style={{ color: '#6610f2' }} />;
    }
};

// ── Component ─────────────────────────────────────────────────────────────────

const YearEndClosing: React.FC = () => {
    const { t } = useTranslation(['common', 'screens']);

    const [initialized,          setInitialized]          = useState(false);
    const [loading,              setLoading]              = useState(false);
    const [closing,              setClosing]              = useState(false);
    const [unclosing,            setUnclosing]            = useState(false);
    const [error,                setError]                = useState<string | null>(null);
    const [fiscalYears,          setFiscalYears]          = useState<FiscalYear[]>([]);
    const [selectedFiscalYearId, setSelectedFiscalYearId] = useState('');
    const [status,               setStatus]               = useState<FiscalYearClosingStatus | null>(null);
    const [showConfirm,          setShowConfirm]          = useState(false);
    const [showUncloseConfirm,   setShowUncloseConfirm]   = useState(false);

    const provider = useMemo(() => new FiscalYearRestDataProvider(), []);

    // ── Phase 1: fiscal years ─────────────────────────────────────────────────

    useEffect(() => {
        provider.getAll()
            .then(years => {
                const sorted = [...years].sort(
                    (a, b) => new Date(b.start).getTime() - new Date(a.start).getTime()
                );
                setFiscalYears(sorted);
                if (sorted.length > 0) setSelectedFiscalYearId(sorted[0].id);
                setInitialized(true);
            })
            .catch(e => setError((e as Error).message));
    }, [provider]);

    // ── Phase 2: closing status ───────────────────────────────────────────────

    const loadStatus = useCallback(async () => {
        if (!selectedFiscalYearId) return;
        setLoading(true);
        setError(null);
        setStatus(null);
        try {
            const s = await provider.getClosingStatus(selectedFiscalYearId);
            setStatus(s);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setLoading(false);
        }
    }, [provider, selectedFiscalYearId]);

    useEffect(() => {
        if (initialized) loadStatus();
    }, [loadStatus, initialized]);

    // ── Derived ───────────────────────────────────────────────────────────────

    const selectedFiscalYear = useMemo(
        () => fiscalYears.find(fy => fy.id === selectedFiscalYearId) ?? null,
        [fiscalYears, selectedFiscalYearId]
    );

    const checks = useMemo((): CheckItem[] => {
        if (!status) return [];
        const items: CheckItem[] = [];

        // 1 — BTW Aangifte
        items.push({
            key: 'vat',
            title: t('screens:yearEndClosing.checks.vatTitle'),
            description: status.allVatReturnsBooked
                ? t('screens:yearEndClosing.checks.vatPass')
                : t('screens:yearEndClosing.checks.vatFail', {
                      count: status.unbookedVatQuarters.length,
                  }),
            result: status.allVatReturnsBooked ? 'pass' : 'fail',
            badge: status.allVatReturnsBooked
                ? t('screens:yearEndClosing.pass')
                : t('screens:yearEndClosing.checks.vatBadge', {
                      quarters: status.unbookedVatQuarters.join(', '),
                  }),
            details: !status.allVatReturnsBooked ? status.unbookedVatQuarters : undefined,
        });

        // 2 — Vaste Activa
        items.push({
            key: 'assets',
            title: t('screens:yearEndClosing.checks.assetsTitle'),
            description: status.allFixedAssetsBooked
                ? t('screens:yearEndClosing.checks.assetsPass')
                : t('screens:yearEndClosing.checks.assetsFail', {
                      count: status.unbookedFixedAssetCount,
                  }),
            result: status.allFixedAssetsBooked ? 'pass' : 'warn',
            badge: status.allFixedAssetsBooked
                ? t('screens:yearEndClosing.pass')
                : t('screens:yearEndClosing.checks.assetsCount', {
                      count: status.unbookedFixedAssetCount,
                  }),
        });

        // 3 — Afschrijvingen
        items.push({
            key: 'depr',
            title: t('screens:yearEndClosing.checks.deprTitle'),
            description: status.allDepreciationsBooked
                ? t('screens:yearEndClosing.checks.deprPass')
                : t('screens:yearEndClosing.checks.deprFail', {
                      count: status.missingDepreciationAssets.length,
                  }),
            result: status.allDepreciationsBooked ? 'pass' : 'fail',
            badge: status.allDepreciationsBooked
                ? t('screens:yearEndClosing.pass')
                : t('screens:yearEndClosing.checks.deprBadge', {
                      count: status.missingDepreciationAssets.length,
                  }),
            details: !status.allDepreciationsBooked
                ? status.missingDepreciationAssets
                : undefined,
        });

        // 4 — Inkoopfacturen
        items.push({
            key: 'invoices',
            title: t('screens:yearEndClosing.checks.invoicesTitle'),
            description: status.allSupplierInvoicesBooked
                ? t('screens:yearEndClosing.checks.invoicesPass')
                : t('screens:yearEndClosing.checks.invoicesFail', {
                      count: status.unbookedSupplierInvoiceCount,
                  }),
            result: status.allSupplierInvoicesBooked ? 'pass' : 'warn',
            badge: status.allSupplierInvoicesBooked
                ? t('screens:yearEndClosing.pass')
                : t('screens:yearEndClosing.checks.invoicesCount', {
                      count: status.unbookedSupplierInvoiceCount,
                  }),
        });

        // 5 — Balans
        items.push({
            key: 'balance',
            title: t('screens:yearEndClosing.checks.balanceTitle'),
            description: status.isBalanced
                ? t('screens:yearEndClosing.checks.balancePass')
                : t('screens:yearEndClosing.checks.balanceFail', {
                      diff: fmtEuro(status.balanceDiff),
                  }),
            result: status.isBalanced ? 'pass' : 'fail',
            badge: status.isBalanced
                ? t('screens:yearEndClosing.pass')
                : t('screens:yearEndClosing.checks.balanceBadge', {
                      diff: fmtEuro(status.balanceDiff),
                  }),
        });

        // 6 — Afsluitboekingen (already done?)
        items.push({
            key: 'closing',
            title: t('screens:yearEndClosing.checks.closingTitle'),
            description: status.hasClosingEntries
                ? t('screens:yearEndClosing.checks.closingDone')
                : t('screens:yearEndClosing.checks.closingPending'),
            result: status.hasClosingEntries ? 'pass' : 'info',
            badge: status.hasClosingEntries
                ? t('screens:yearEndClosing.checks.closingDoneBadge')
                : t('screens:yearEndClosing.checks.closingPendingBadge'),
        });

        // 7 — Privé Opnames / Stortingen
        const hasPriveMovements =
            Math.abs(status.priveOpnamesAmount) >= 0.01 ||
            Math.abs(status.priveStortingenAmount) >= 0.01;
        items.push({
            key: 'prive',
            title: t('screens:yearEndClosing.checks.priveTitle'),
            description: hasPriveMovements
                ? t('screens:yearEndClosing.checks.priveDesc', {
                      opnames:    fmtEuro(status.priveOpnamesAmount),
                      stortingen: fmtEuro(status.priveStortingenAmount),
                  })
                : t('screens:yearEndClosing.checks.priveNone'),
            result: 'info',
            badge: hasPriveMovements
                ? t('screens:yearEndClosing.checks.priveBadge', {
                      net: fmtEuro(status.priveStortingenAmount - status.priveOpnamesAmount),
                  })
                : t('screens:yearEndClosing.checks.priveNoneBadge'),
        });

        return items;
    }, [status, t]);

    const canClose = status != null
        && !status.isAlreadyClosed
        && !status.hasClosingEntries
        && !closing;

    const allCriticalPass = status != null
        && status.allVatReturnsBooked
        && status.allDepreciationsBooked
        && status.isBalanced;

    // ── Actions ───────────────────────────────────────────────────────────────

    const handleClose = async () => {
        setShowConfirm(false);
        setClosing(true);
        setError(null);
        try {
            await provider.closeFiscalYear(selectedFiscalYearId);
            await loadStatus();
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setClosing(false);
        }
    };

    const handleUnclose = async () => {
        setShowUncloseConfirm(false);
        setUnclosing(true);
        setError(null);
        try {
            await provider.uncloseFiscalYear(selectedFiscalYearId);
            // Reload fiscal years so the state badge in the selector updates too.
            const years = await provider.getAll();
            const sorted = [...years].sort(
                (a, b) => new Date(b.start).getTime() - new Date(a.start).getTime()
            );
            setFiscalYears(sorted);
            await loadStatus();
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setUnclosing(false);
        }
    };

    // ── Toolbar ───────────────────────────────────────────────────────────────

    const filters: ToolbarFilterConfig[] = [
        {
            key: 'fiscal-year',
            type: 'custom',
            label: t('common:fiscalYear'),
            render: () => (
                <select
                    className="toolbar__filter-select"
                    value={selectedFiscalYearId}
                    onChange={e => setSelectedFiscalYearId(e.target.value)}
                    disabled={!initialized}
                >
                    {!initialized
                        ? <option value="">{t('common:loading')}</option>
                        : fiscalYears.map(fy => (
                            <option key={fy.id} value={fy.id}>
                                {fy.name}
                                {fy.state === FiscalYearState.Closed
                                    ? ` (${t('screens:yearEndClosing.stateClosed')})`
                                    : ''}
                            </option>
                          ))
                    }
                </select>
            ),
        } as ToolbarFilterConfig,
    ];

    const buttons: ToolbarButtonConfig[] = [
        {
            key: 'refresh',
            icon: <FaSync />,
            label: t('screens:yearEndClosing.refresh'),
            variant: 'secondary',
            onClick: loadStatus,
            disabled: loading || !selectedFiscalYearId,
        },
    ];

    // ── Stats ─────────────────────────────────────────────────────────────────

    const netResult = status?.nettoresultaatPreview ?? 0;
    const statsItems = status
        ? [
            {
                key: 'result',
                label: t('screens:yearEndClosing.netResult'),
                value: fmtEuro(netResult),
                variant: (netResult >= 0 ? 'positive' : 'negative') as 'positive' | 'negative',
            },
            {
                key: 'vatStatus',
                label: t('screens:yearEndClosing.vatStatus'),
                value: status.allVatReturnsBooked
                    ? t('screens:yearEndClosing.allBooked')
                    : `${4 - status.unbookedVatQuarters.length}/4`,
            },
            {
                key: 'assetsStatus',
                label: t('screens:yearEndClosing.assetsStatus'),
                value: status.unbookedFixedAssetCount === 0
                    ? t('screens:yearEndClosing.allBooked')
                    : `${status.unbookedFixedAssetCount} ${t('screens:yearEndClosing.pending')}`,
            },
            {
                key: 'balanceStatus',
                label: t('screens:yearEndClosing.balanceStatus'),
                value: status.isBalanced
                    ? t('screens:yearEndClosing.balanced')
                    : t('screens:yearEndClosing.imbalanced'),
                variant: (status.isBalanced ? 'positive' : 'negative') as 'positive' | 'negative',
            },
        ]
        : [];

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div className="year-end-closing-page">
            <MainScreenHeader
                title={t('screens:yearEndClosing.title')}
                subtitle={selectedFiscalYear
                    ? `${selectedFiscalYear.name} · ${
                        selectedFiscalYear.state === FiscalYearState.Closed
                            ? t('screens:yearEndClosing.stateClosed')
                            : t('screens:yearEndClosing.stateOpen')
                      }`
                    : undefined}
            />

            <Toolbar filters={filters} buttons={buttons} />

            {error && <ErrorMessage message={error} />}

            {status && <StatsBar items={statsItems} flush />}

            <div className="yec-scroll">

                {loading && (
                    <div className="yec-loading">{t('screens:yearEndClosing.loading')}</div>
                )}

                {!loading && status && (
                    <>
                        <p className="yec-intro">
                            {t('screens:yearEndClosing.intro')}
                        </p>

                        {/* Net result banner */}
                        <div className={`yec-result-banner${netResult < 0 ? ' yec-result-banner--loss' : ''}`}>
                            <div>
                                <div className="yec-result-label">
                                    {t('screens:yearEndClosing.nettoresultaat')}
                                    &nbsp;—&nbsp;{selectedFiscalYear?.name}
                                </div>
                                <div className="yec-result-amount">{fmtEuro(netResult)}</div>
                            </div>
                            <div className="yec-result-note">
                                {netResult >= 0
                                    ? t('screens:yearEndClosing.profitNote')
                                    : t('screens:yearEndClosing.lossNote')}
                            </div>
                        </div>

                        {/* Checklist */}
                        <div className="yec-checklist">
                            {checks.map(check => (
                                <div
                                    key={check.key}
                                    className={`yec-check-card yec-check-card--${check.result}`}
                                >
                                    <div className="yec-check-icon">
                                        {resultIcon(check.result)}
                                    </div>
                                    <div className="yec-check-body">
                                        <div className="yec-check-title">{check.title}</div>
                                        <div className="yec-check-desc">
                                            {check.description}
                                            {check.details && check.details.length > 0 && (
                                                <ul>
                                                    {check.details.map(d => (
                                                        <li key={d}>{d}</li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                        {check.badge && (
                                            <span className={`yec-check-badge yec-check-badge--${check.result === 'pass' ? 'pass' : check.result === 'fail' ? 'fail' : 'warn'}`}>
                                                {check.badge}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>

            {/* Sticky footer action area */}
            {status && (
                <div className="yec-footer">
                    <div className="yec-footer-note">
                        {status.isAlreadyClosed
                            ? t('screens:yearEndClosing.footerClosed')
                            : status.hasClosingEntries
                            ? t('screens:yearEndClosing.footerAlreadyBooked')
                            : allCriticalPass
                            ? t('screens:yearEndClosing.footerReady')
                            : t('screens:yearEndClosing.footerWarnings')}
                    </div>

                    {status.isAlreadyClosed ? (
                        <div className="yec-closed-row">
                            <div className="yec-already-closed">
                                <FaCheckCircle />
                                {t('screens:yearEndClosing.alreadyClosed')}
                            </div>
                            <button
                                className="yec-unclose-btn"
                                disabled={unclosing}
                                onClick={() => setShowUncloseConfirm(true)}
                            >
                                <FaLockOpen />
                                {unclosing
                                    ? t('screens:yearEndClosing.unclosing')
                                    : t('screens:yearEndClosing.uncloseBtn')}
                            </button>
                        </div>
                    ) : (
                        <button
                            className={`yec-close-btn ${allCriticalPass ? 'yec-close-btn--ready' : 'yec-close-btn--warn'}`}
                            disabled={!canClose}
                            onClick={() => setShowConfirm(true)}
                        >
                            <FaLock />
                            {closing
                                ? t('screens:yearEndClosing.closing')
                                : t('screens:yearEndClosing.closeBtn')}
                        </button>
                    )}
                </div>
            )}

            {/* Confirmation dialog */}
            {showConfirm && status && (
                <div className="yec-confirm-overlay" onClick={() => setShowConfirm(false)}>
                    <div className="yec-confirm-box" onClick={e => e.stopPropagation()}>
                        <div className="yec-confirm-title">
                            {t('screens:yearEndClosing.confirmTitle')}
                        </div>
                        <div className="yec-confirm-body">
                            <p>{t('screens:yearEndClosing.confirmBody', {
                                year: selectedFiscalYear?.name ?? '',
                                amount: fmtEuro(Math.abs(netResult)),
                                type: netResult >= 0
                                    ? t('screens:yearEndClosing.profit')
                                    : t('screens:yearEndClosing.loss'),
                            })}</p>
                            <p>{t('screens:yearEndClosing.confirmActions')}</p>
                            <ul>
                                <li>{t('screens:yearEndClosing.confirmAction1')}</li>
                                <li>{t('screens:yearEndClosing.confirmAction2')}</li>
                                <li>{t('screens:yearEndClosing.confirmAction3')}</li>
                                {(Math.abs(status.priveOpnamesAmount) >= 0.01 ||
                                  Math.abs(status.priveStortingenAmount) >= 0.01) && (
                                    <li>{t('screens:yearEndClosing.confirmAction4')}</li>
                                )}
                            </ul>
                            {!allCriticalPass && (
                                <p style={{ color: '#856404', background: '#fff3cd', padding: '0.5rem 0.75rem', borderRadius: 6, marginTop: '0.75rem' }}>
                                    ⚠ {t('screens:yearEndClosing.confirmWarning')}
                                </p>
                            )}
                        </div>
                        <div className="yec-confirm-actions">
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => setShowConfirm(false)}
                            >
                                {t('common:cancel')}
                            </button>
                            <button
                                className="btn btn-primary btn-sm"
                                style={{ background: '#6610f2', borderColor: '#6610f2', color: '#fff' }}
                                onClick={handleClose}
                            >
                                <FaLock style={{ marginRight: 5 }} />
                                {t('screens:yearEndClosing.confirmClose')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Undo closure confirmation dialog */}
            {showUncloseConfirm && (
                <div className="yec-confirm-overlay" onClick={() => setShowUncloseConfirm(false)}>
                    <div className="yec-confirm-box" onClick={e => e.stopPropagation()}>
                        <div className="yec-confirm-title">
                            {t('screens:yearEndClosing.uncloseConfirmTitle')}
                        </div>
                        <div className="yec-confirm-body">
                            <p>{t('screens:yearEndClosing.uncloseConfirmBody', {
                                year: selectedFiscalYear?.name ?? '',
                            })}</p>
                            <ul>
                                <li>{t('screens:yearEndClosing.uncloseAction1')}</li>
                                <li>{t('screens:yearEndClosing.uncloseAction2')}</li>
                            </ul>
                            <p style={{ color: '#856404', background: '#fff3cd', padding: '0.5rem 0.75rem', borderRadius: 6, marginTop: '0.75rem' }}>
                                ⚠ {t('screens:yearEndClosing.uncloseWarning')}
                            </p>
                        </div>
                        <div className="yec-confirm-actions">
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => setShowUncloseConfirm(false)}
                            >
                                {t('common:cancel')}
                            </button>
                            <button
                                className="btn btn-warning btn-sm"
                                onClick={handleUnclose}
                            >
                                <FaLockOpen style={{ marginRight: 5 }} />
                                {t('screens:yearEndClosing.uncloseConfirmClose')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default YearEndClosing;


